import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RegionBootstrapAction,
  RegionBootstrapResultDto,
} from "@/types/admin-region";
import { cn } from "@/lib/utils";

interface BootstrapResultDialogProps {
  result: RegionBootstrapResultDto | null;
  onClose: () => void;
}

const ACTION_BADGE_CLASS: Record<RegionBootstrapAction, string> = {
  created: "border-light-green/50 text-light-green bg-light-green/5",
  updated: "border-skyline-2/50 text-skyline-2 bg-skyline-2/5",
  unchanged: "border-base-border text-muted-foreground bg-secondary-badge",
  deactivated: "border-light-orange/50 text-light-orange bg-light-orange/5",
  deleted:
    "border-custom-destructive-dark/50 text-custom-destructive-dark bg-custom-destructive-dark/5",
  skipped: "border-base-border text-zinc-400 bg-secondary-badge",
};

export const BootstrapResultDialog: React.FC<BootstrapResultDialogProps> = ({
  result,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!result) return null;

  const summary = result.summary;
  const ranAt = format(new Date(result.ranAt), "PP HH:mm:ss");

  return (
    <Dialog open={!!result} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("regions.bootstrap.successTitle")}</DialogTitle>
          <DialogDescription>
            {t("regions.bootstrap.successDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>{t("regions.bootstrap.ranAt", { time: ranAt })}</span>
            <span>·</span>
            <span>
              {t("regions.bootstrap.envCount", { count: result.envCount })}
            </span>
            <span>·</span>
            <span>
              {t("regions.bootstrap.dbCount", { count: result.dbCount })}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {(
              [
                "created",
                "updated",
                "unchanged",
                "deactivated",
                "deleted",
                "skipped",
              ] as RegionBootstrapAction[]
            ).map((action) => (
              <Badge
                key={action}
                className={cn("border", ACTION_BADGE_CLASS[action])}
              >
                {t(`regions.bootstrap.summary.${action}`, {
                  count: summary[action],
                })}
              </Badge>
            ))}
          </div>
          <div className="overflow-x-auto rounded-md border border-base-border">
            <Table>
              <TableHeader>
                <TableRow className="h-10">
                  <TableHead>{t("regions.bootstrap.table.code")}</TableHead>
                  <TableHead>{t("regions.bootstrap.table.action")}</TableHead>
                  <TableHead>
                    {t("regions.bootstrap.table.envPrefix")}
                  </TableHead>
                  <TableHead>{t("regions.bootstrap.table.reason")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.entries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-16 text-center text-muted-foreground"
                    >
                      {t("regions.bootstrap.table.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  result.entries.map((entry, index) => (
                    <TableRow key={`${entry.code}-${index}`}>
                      <TableCell className="font-mono text-xs">
                        {entry.code}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "border",
                            ACTION_BADGE_CLASS[entry.action],
                          )}
                        >
                          {t(`regions.bootstrap.actions.${entry.action}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {entry.envPrefix ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {entry.reason ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="new_outline" onClick={onClose}>
            {t("regions.bootstrap.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
